import { Box } from '@mui/material';
import type { IAppDetailsAppData, IPlatform } from '../common-types';
import { platformNameConverter } from '../common-utilities';
import {
  ThresholdImageWidget,
  type ThresholdImageWidgetProps,
} from '../ThresholdImageWidget';
import type { ImageProps } from 'next/image';
import { ProgressBarScore } from '../ProgressBarScore';

export type PlatformThresholdSummaryProps = IPlatform & {
  app: IAppDetailsAppData;
  FlexibleImageComponent: React.ComponentType<ImageProps>;
  translatedText: {
    thresholdImageWidgetTooltipText: string;
  };
  configuration: ThresholdImageWidgetProps;
};

export const PlatformThresholdSummary = ({
  platformName,
  app,
  configuration,
  translatedText,
  FlexibleImageComponent,
}: PlatformThresholdSummaryProps) => {
  const { platforms } = app ?? {};

  const relevantPlatform = platforms?.find(
    (p) => platformNameConverter(p.platformName) === platformName
  );

  if (!relevantPlatform) {
    return null;
  }

  const title = configuration?.title || 'Threshold Image';
  const scoreValue =
    configuration?.value || relevantPlatform?.scoreSummary?.scoreValue || 0;
  const thresholds = configuration?.thresholds || [];

  const renderScoreSections = () => {
    return (
      relevantPlatform?.scoreSections &&
      relevantPlatform?.scoreSections?.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gap: (theme) => theme.spacing(2),
          }}
        >
          {relevantPlatform.scoreSections?.map((section) => {
            const { name, scoreValue: sectionScore } = section;
            return (
              <Box key={name}>
                <ProgressBarScore
                  label={name ?? ''}
                  score={Math.round(sectionScore ?? 0)}
                  thresholds={thresholds}
                  FlexibleImageComponent={FlexibleImageComponent}
                />
              </Box>
            );
          })}
        </Box>
      )
    );
  };

  return (
    <>
      <ThresholdImageWidget
        title={title}
        value={scoreValue}
        thresholds={thresholds}
        FlexibleImageComponent={FlexibleImageComponent}
        styleOverrides={configuration?.styleOverrides}
        translatedText={{
          tooltipText: translatedText.thresholdImageWidgetTooltipText,
        }}
      />

      {renderScoreSections()}
    </>
  );
};
